import { useCheckoutTracking } from '@checkout/core'
import { useCallback, useEffect, useState } from 'react'

import styles from './Toast.module.css'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface ToastProps {
  id: string
  type: ToastType
  message: string
  duration?: number
  onClose: (id: string) => void
}

const ICONS: Record<ToastType, string> = {
  success: '✓',
  error: '✕',
  warning: '⚠',
  info: 'ℹ',
}

export function Toast({ id, type, message, duration = 5000, onClose }: ToastProps) {
  const [isExiting, setIsExiting] = useState(false)
  const { trackEvent } = useCheckoutTracking()

  const handleClose = useCallback(
    (isAutoClose = false) => {
      // Track close event
      trackEvent(isAutoClose ? 'toast_auto_dismiss' : 'toast_close_click', 'toast', {
        toast_type: type,
        toast_message: message,
      })

      setIsExiting(true)
      setTimeout(() => {
        onClose(id)
      }, 300) // Match animation duration
    },
    [id, onClose, trackEvent, type, message]
  )

  useEffect(() => {
    if (duration <= 0) {
      return
    }
    const timer = setTimeout(() => handleClose(true), duration)
    return () => clearTimeout(timer)
  }, [duration, handleClose])

  return (
    <div
      className={`${styles.toast} ${styles[type]} ${isExiting ? styles.exiting : ''}`}
      role="alert"
      aria-live="polite"
    >
      <span className={styles.icon}>{ICONS[type]}</span>
      <span className={styles.message}>{message}</span>
      <button className={styles.closeButton} onClick={() => handleClose()} aria-label="Close">
        ✕
      </button>
    </div>
  )
}
